/**
 * URL Sync Service
 * 
 * Synchronizes XState application state with the browser URL.
 * Handles bidirectional sync: URL changes update state, state changes update URL.
 */

import { decodePermalink, encodePermalink } from '../utils/permalinkUtils';
import { getActivePanel } from '../utils/getActivePanel';
import { validateDeeplink } from '../utils/deeplinkValidation';
import interactionLedger from '../utils/interactionLedger';
import logger from '../logger';

/**
 * Creates a URL sync service that connects XState app state to the browser URL
 * @param {Object} options - Configuration options
 * @param {Function} options.sendAppState - Function to send events to XState app state machine
 * @param {Function} options.getAppState - Function to get current app state context
 * @returns {Object} Service object with start/stop methods
 */
export const createUrlSyncService = ({ sendAppState, getAppState }) => {
  let isInitialized = false;
  let isUpdatingFromState = false;
  let isUpdatingFromUrl = false;
  let unsubscribeState = null;
  let unsubscribeHistory = null;
  let lastUrlUpdateFromState = 0; // Timestamp of last URL update from state
  let hasRestoredFromUrl = false; // Track if we've already restored from URL on initial load
  let lastUpdatedPath = null; // Track the last path we updated to prevent duplicate updates
  let urlUpdateTimeout = null; // Timeout for debouncing URL updates
  let lastActivePanels = null; // Track last set of active panels for panel selection logging

  /**
   * Updates the URL based on current app state
   * Always builds URL from XState values to ensure they stay in sync
   */
  const updateUrlFromState = () => {
    if (isUpdatingFromUrl || !isInitialized) {
      logger.debug({ isUpdatingFromUrl, isInitialized }, 'Skipping URL update from state');
      return;
    }

    try {
      isUpdatingFromState = true;
      // Always get fresh state from XState - never use cached or URL values
      const appState = getAppState();
      
      // Extract active panels from FlexLayout model (returns array of panel IDs)
      const activePanels = getActivePanel(appState);

      // Log panel selection changes to the interaction ledger
      try {
        const previousPanels = Array.isArray(lastActivePanels)
          ? lastActivePanels
          : [];
        const currentPanels = Array.isArray(activePanels) ? activePanels : [];

        const panelsChanged =
          JSON.stringify(previousPanels) !== JSON.stringify(currentPanels);

        if (panelsChanged) {
          // Map internal panel identifiers to human-friendly names
          const panelDisplayMap = {
            info: 'Info',
            notes: 'Notes',
            editor: 'Editor',
            notebooks: 'Notebooks',
            aof: 'Areas of Focus',
            'open-notes': 'Open Notes',
          };

          const previousLabels =
            previousPanels.length > 0
              ? previousPanels
                  .map((panelId) => panelDisplayMap[panelId] || panelId)
                  .join(', ')
              : 'none';

          const currentLabels =
            currentPanels.length > 0
              ? currentPanels
                  .map((panelId) => panelDisplayMap[panelId] || panelId)
                  .join(', ')
              : 'none';

          interactionLedger.log(
            'panelSelection',
            'ui',
            {
              previousPanels,
              currentPanels,
            },
            `Panels changed: ${previousLabels} → ${currentLabels}`
          );

          lastActivePanels = currentPanels;
        }
      } catch (panelLogError) {
        // Never let panel logging break URL sync
        logger.error(
          { error: panelLogError },
          'Failed to log panel selection change to interaction ledger'
        );
      }
      
      logger.debug({ 
        activePanels, 
        hasFlexLayoutModel: !!appState.activeFlexLayoutModel,
        selectedNoteId: appState.selectedNoteId,
        selectedAreaOfFocusId: appState.selectedAreaOfFocusId,
        selectedNotebookId: appState.selectedNotebookId
      }, 'URL sync: Extracting state for permalink (always from XState)');
      
      // Build state object from XState values only
      // This ensures URL always reflects XState, never the other way around
      const state = {
        selectedNoteId: appState.selectedNoteId || null,
        selectedNotebookId: appState.selectedNotebookId || null,
        selectedAreaOfFocusId: appState.selectedAreaOfFocusId || null,
        activePanel: activePanels.length > 0 ? activePanels : null, // Array of panel IDs or null
        searchQuery: appState.searchQuery || null,
        itemStatus: appState.itemStatus || 'notes',
      };

      // Encode permalink from XState values
      const newPath = encodePermalink(state);
      
      // Get current path from window.location (works with Next.js)
      const currentPath = typeof window !== 'undefined'
        ? window.location.pathname + window.location.search
        : '/';

      // Prevent duplicate updates - if we just updated to this path, skip
      // But allow note changes to always update (user clicking different notes)
      const isNotePath = newPath.startsWith('/e/');
      if (lastUpdatedPath === newPath && !isNotePath) {
        logger.debug({ newPath, currentPath }, 'Skipping duplicate URL update');
        return;
      }

      // Check if this is a note selection change (should update immediately)
      const currentPathname = typeof window !== 'undefined' ? window.location.pathname : '/';
      const newPathname = newPath.split('?')[0];
      const isNoteChange = currentPathname !== newPathname && newPathname.startsWith('/e/');
      
      // Throttle URL updates to prevent too frequent changes
      // But allow immediate updates for note selection changes
      const timeSinceLastUpdate = Date.now() - lastUrlUpdateFromState;
      if (!isNoteChange && timeSinceLastUpdate < 300 && lastUrlUpdateFromState > 0) {
        // Debounce rapid updates - but skip throttling for note changes
        if (urlUpdateTimeout) {
          clearTimeout(urlUpdateTimeout);
        }
        urlUpdateTimeout = setTimeout(() => {
          updateUrlFromState();
        }, 300 - timeSinceLastUpdate);
        return;
      }
      
      // If pathname is changing (but not a note change), be more careful to prevent Next.js navigation
      if (currentPathname !== newPathname && !isNoteChange) {
        // Pathname is changing - add extra delay to prevent rapid navigation
        if (timeSinceLastUpdate < 500) {
          if (urlUpdateTimeout) {
            clearTimeout(urlUpdateTimeout);
          }
          urlUpdateTimeout = setTimeout(() => {
            updateUrlFromState();
          }, 500 - timeSinceLastUpdate);
          return;
        }
      }

      // Always update URL to match XState
      // This ensures URL reflects current XState state
      if (newPath !== currentPath) {
        logger.info({
          newPath,
          currentPath,
          selectedNoteId: appState.selectedNoteId,
          selectedAreaOfFocusId: appState.selectedAreaOfFocusId,
          selectedNotebookId: appState.selectedNotebookId,
          activePanels
        }, 'Updating URL from XState (client-side routing, no page reload)');

        // Track URL update in ledger
        interactionLedger.log(
          'urlUpdate',
          'urlSync',
          {
            from: currentPath,
            to: newPath,
            selectedNoteId: appState.selectedNoteId,
            selectedAreaOfFocusId: appState.selectedAreaOfFocusId,
            selectedNotebookId: appState.selectedNotebookId,
          },
          `URL updated from state: ${currentPath} → ${newPath}`
        );

        // Record timestamp and path before the update
        lastUrlUpdateFromState = Date.now();
        lastUpdatedPath = newPath;

        // Update URL using window.history.replaceState directly.
        // We call it exactly once — no verification loop, which would cause a second
        // replaceState call and produce an intermediate URL flash in the browser.
        if (typeof window !== 'undefined') {
          window.history.replaceState({}, '', newPath);
        }
      } else {
        logger.debug({
          newPath,
          currentPath,
          selectedNoteId: appState.selectedNoteId,
          selectedAreaOfFocusId: appState.selectedAreaOfFocusId,
          selectedNotebookId: appState.selectedNotebookId,
          activePanels
        }, 'URL matches XState, no update needed');
      }
    } catch (error) {
      logger.error({ error }, 'Error updating URL from state');
    } finally {
      isUpdatingFromState = false;
    }
  };

  /**
   * Updates app state based on current URL
   * ONLY called on initial page load - after that, URL is one-way (state → URL)
   * User interactions update state, which then updates URL
   */
  const updateStateFromUrl = async () => {
    // CRITICAL: Only restore from URL on initial page load
    // After initial load, URL changes should NEVER trigger state updates
    // All state changes come from user interactions, which then update the URL
    if (hasRestoredFromUrl) {
      logger.debug('Skipping state update from URL - already restored on initial load. URL is now one-way (state → URL only)');
      return;
    }
    
    // Don't update state from URL if we're currently updating URL from state
    // This prevents bidirectional sync loops
    if (isUpdatingFromState || !isInitialized) {
      logger.debug({ isUpdatingFromState, isInitialized }, 'Skipping state update from URL (updating from state or not initialized)');
      return;
    }

    try {
      isUpdatingFromUrl = true;
      
      // Get location from window (works with Next.js)
      if (typeof window === 'undefined') {
        return;
      }
      
      const { pathname, search } = window.location;
      const fullUrl = window.location.href;
      const urlState = decodePermalink(pathname, search);
      
      logger.debug({ pathname, search, urlState }, 'Updating state from URL');

      const appState = getAppState();

      // Only process deeplink if app is primed
      if (!appState.applicationPrimed) {
        logger.debug('App not primed yet, skipping URL state restoration');
        isUpdatingFromUrl = false; // Reset flag so URL sync can happen
        return;
      }

      // Validate deeplink if it contains IDs
      if (urlState.selectedNoteId || urlState.selectedNotebookId || urlState.selectedAreaOfFocusId) {
        const validation = await validateDeeplink(appState, urlState);
        
        if (!validation.valid) {
          // Deeplink is invalid - show error modal and redirect to home
          logger.warn({ validation, urlState }, 'Invalid deeplink detected');
          
          // Store the broken link in app state
          sendAppState({
            type: 'setDeeplinkError',
            data: {
              url: fullUrl,
              reason: validation.reason,
              invalidId: validation.invalidId,
            },
          });

          // Select default note and AOF FIRST (before redirect)
          // This ensures the app has something to show
          const defaultNoteId = appState.notesList && appState.notesList.length > 0 
            ? appState.notesList[0].note_id 
            : null;
          const defaultAofId = appState.areasOfFocusCollectionsIterable && appState.areasOfFocusCollectionsIterable.length > 0
            ? appState.areasOfFocusCollectionsIterable[0].area_of_focus_collection_id
            : null;

          if (defaultNoteId) {
            sendAppState({
              type: 'selectNote',
              data: {
                noteId: defaultNoteId,
              },
            });
          }
          
          if (defaultAofId) {
            sendAppState({
              type: 'selectAreasOfFocusCollection',
              areaOfFocusCollectionId: defaultAofId,
            });
          }

          // Wait a moment for state to update, then redirect
          setTimeout(() => {
            // Redirect to home page using window.history to avoid Next.js navigation events
            if (typeof window !== 'undefined') {
              window.history.replaceState({}, '', '/');
            }
          }, 100);

          return;
        }
      }

      // Only restore state if it's different from current state
      // This prevents unnecessary updates when URL was just updated from state
      // CRITICAL: Double-check that we're not in the middle of updating from state
      // This prevents race conditions where URL updates before state fully commits
      if (isUpdatingFromState) {
        logger.debug('Skipping state restoration - currently updating URL from state');
        return;
      }
      
      // Also check if URL was just updated from state (within last 500ms)
      // This prevents race conditions where history listener fires after URL update
      const timeSinceLastUrlUpdate = Date.now() - lastUrlUpdateFromState;
      if (timeSinceLastUrlUpdate < 500) {
        logger.debug({ timeSinceLastUrlUpdate }, 'Skipping state restoration - URL was just updated from state');
        return;
      }
      
      // Restore selected note - only if different AND we're sure it's from browser navigation
      if (urlState.selectedNoteId && urlState.selectedNoteId !== appState.selectedNoteId) {
        logger.info({ 
          urlNoteId: urlState.selectedNoteId, 
          currentNoteId: appState.selectedNoteId,
          isUpdatingFromState 
        }, 'Restoring note from URL (browser navigation)');
        sendAppState({
          type: 'selectNote',
          data: {
            noteId: urlState.selectedNoteId,
          },
        });
      }

      // Restore selected notebook - only if different
      if (urlState.selectedNotebookId && urlState.selectedNotebookId !== appState.selectedNotebookId) {
        logger.debug({ 
          urlNotebookId: urlState.selectedNotebookId, 
          currentNotebookId: appState.selectedNotebookId 
        }, 'Restoring notebook from URL');
        sendAppState({
          type: 'selectNotebook',
          notebookId: urlState.selectedNotebookId,
        });
      }

      // Restore selected area of focus - only if different
      if (urlState.selectedAreaOfFocusId && urlState.selectedAreaOfFocusId !== appState.selectedAreaOfFocusId) {
        logger.debug({ 
          urlAofId: urlState.selectedAreaOfFocusId, 
          currentAofId: appState.selectedAreaOfFocusId 
        }, 'Restoring AOF from URL');
        sendAppState({
          type: 'selectAreasOfFocusCollection',
          areaOfFocusCollectionId: urlState.selectedAreaOfFocusId,
        });
      }

      // Restore item status (notes vs trash)
      if (urlState.itemStatus && urlState.itemStatus !== appState.itemStatus) {
        // This might need a specific action, depending on your state machine
        // For now, we'll handle it through route changes
      }

      // Restore active panels (support multiple panels)
      if (urlState.activePanel && urlState.activePanel.length > 0) {
        // Map panel identifier back to FlexLayout component name
        const panelToComponentMap = {
          'info': 'info',
          'notes': 'notes',
          'editor': 'editorWrapper',
          'notebooks': 'notebooks',
          'aof': 'aofCollections',
          'open-notes': 'openNotesPanel',
        };
        
        // Handle both array and single value for backwards compatibility
        const panelsToRestore = Array.isArray(urlState.activePanel) 
          ? urlState.activePanel 
          : [urlState.activePanel];
        
        const currentActivePanels = getActivePanel(appState);
        
        // Restore each panel that's not already active
        panelsToRestore.forEach(panelId => {
          const componentName = panelToComponentMap[panelId] || panelId;
          
          // Only select if it's not already active
          if (!currentActivePanels.includes(panelId)) {
            // Send action to select the panel tab via DryChrome machine
            sendAppState({
              type: 'SelectTab',
              tabName: componentName,
            });
          }
        });
      }

      // Restore search query
      if (urlState.searchQuery) {
        sendAppState({
          type: 'setSearchQuery',
          query: urlState.searchQuery,
        });
      }

      // Clear any existing deeplink error if we successfully loaded
      if (appState.deeplinkError) {
        sendAppState({
          type: 'clearDeeplinkError',
        });
      }
      
      // Mark that we've restored from URL - this prevents any future URL → state updates
      // After initial load, URL is one-way: state changes update URL, but URL changes don't update state
      hasRestoredFromUrl = true;
      logger.info('Initial state restored from URL - URL is now one-way (state → URL only)');
    } catch (error) {
      logger.error({ error }, 'Error updating state from URL');
      
      // On error, also show deeplink error modal
      const fullUrl = window.location.href;
      sendAppState({
        type: 'setDeeplinkError',
        data: {
          url: fullUrl,
          reason: 'invalid_url',
          invalidId: null,
        },
      });
      
      // Redirect to home using window.history to avoid Next.js navigation events
      if (typeof window !== 'undefined') {
        window.history.replaceState({}, '', '/');
      }
    } finally {
      // Always reset the flag, even on error
      isUpdatingFromUrl = false;
    }
  };

  /**
   * Starts the URL sync service
   */
  const start = () => {
    if (isInitialized) {
      logger.warn('URL sync service already started');
      return;
    }

    logger.info('Starting URL sync service');
    isInitialized = true;

    // Listen to URL changes - but ONLY for logging/debugging
    // We do NOT restore state from URL after initial load
    // URL is one-way: state → URL, never URL → state (except initial load)
    // Use popstate events for browser back/forward navigation
    const handlePopState = () => {
      logger.debug({ location: window.location }, 'URL changed (browser navigation - back/forward) - NOT restoring state (URL is one-way after initial load)');
      // DO NOT call updateStateFromUrl() - URL changes should not trigger state updates
    };
    
    if (typeof window !== 'undefined') {
      window.addEventListener('popstate', handlePopState);
      unsubscribeHistory = () => {
        window.removeEventListener('popstate', handlePopState);
      };
    }

    // Initial state restoration from URL - only if URL is not root
    // If URL is root (/), skip restoration and let XState handle initial state
    // This prevents flashing and ensures clean initial load
    const currentPath = typeof window !== 'undefined'
      ? window.location.pathname
      : '/';
    if (currentPath !== '/') {
      // Only restore from URL if we're not on root
      // Use requestAnimationFrame to ensure it happens after initial render
      requestAnimationFrame(() => {
        updateStateFromUrl();
      });
    } else {
      // Even if we skip restoration (on root), mark that we've "restored" so URL is one-way
      // This ensures URL changes never trigger state updates after initial load
      hasRestoredFromUrl = true;
      logger.info('On root path - skipping URL restoration, URL is now one-way (state → URL only)');
    }

    logger.info('URL sync service started - URL will sync from XState values (one-way after initial load)');
  };

  /**
   * Stops the URL sync service
   */
  const stop = () => {
    if (!isInitialized) {
      return;
    }

    logger.info('Stopping URL sync service');

    if (unsubscribeHistory) {
      unsubscribeHistory();
      unsubscribeHistory = null;
    }

    if (unsubscribeState) {
      unsubscribeState();
      unsubscribeState = null;
    }

    isInitialized = false;
    logger.info('URL sync service stopped');
  };

  /**
   * Manually trigger URL update from current state
   */
  const syncUrl = () => {
    updateUrlFromState();
  };

  /**
   * Manually trigger state update from current URL
   */
  const syncState = () => {
    updateStateFromUrl();
  };

  return {
    start,
    stop,
    syncUrl,
    syncState,
    isInitialized: () => isInitialized,
  };
};

// Note: React hook implementation should be in a separate file
// to keep this service framework-agnostic

